'use client';

import { motion } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { dict, t } from '@/lib/i18n';
import { TextReveal } from './primitives/TextReveal';
import { DiamondIcon, ChartIcon, GlobeIcon } from './icons/FeatureIcons';

// Map each feature slot to a custom animated SVG (replaces emoji).
const icons = [DiamondIcon, ChartIcon, GlobeIcon] as const;

export function Features() {
  const { locale } = useLocale();

  return (
    <section id="features" className="relative py-12 sm:py-16 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold mb-5">
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {t(dict.features.eyebrow, locale)}
          </div>
          <TextReveal as="h2" className="font-serif text-display-sm text-cream leading-[0.98]">
            {t(dict.features.heading, locale)}
          </TextReveal>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {dict.features.items.map((item, i) => {
            const Icon = icons[i] ?? DiamondIcon;
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="group glass glass-sheen rounded-luxe p-7 hover:border-gold/30 hover:translate-y-[-2px] transition-[transform,border-color,box-shadow] duration-300 ease-out"
              >
                <div className="w-16 h-16 mb-6 grid place-items-center transition-transform duration-300 ease-out group-hover:scale-105">
                  <Icon className="w-full h-full" />
                </div>
                <h3 className="font-serif text-xl text-cream mb-3 leading-snug">
                  {t(item.title, locale)}
                </h3>
                <p className="text-fog-300 text-[15px] leading-relaxed">
                  {t(item.body, locale)}
                </p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
